import React from 'react';
import ChatBot from 'react-simple-chatbot';
import { isCNPJValid, isEmailValid, isCPFValid } from '../../utils';
import { ThemeProvider } from 'styled-components';
import { StyledChat, BubbleOptionStyle } from './style';
import { getBusinessAreasOptions, getProfileMeta } from './utils';
import CompanyDifficult from './CompanyDifficult';
import AgreeUseTerm from './AgreeUseTerm';
import LoadingProduct from './LoadingProduct';
import Product from './Product';
import UseTermDialog from './UseTermDialog';
import ServiceRatingSlider from './ServiceRatingSlider';
import BusinessArea from './BusinessArea';
import BotAvatar from '../../assets/chatbot-default.png';
import BotAvatarFloatingIcon from '../../assets/chatbot-appbar.png';
import BotAvatarHappy from '../../assets/chatbot-happy.png';
import BotAvatarStar from '../../assets/chatbot-star.png';
import { fontFamily, primary } from '../App/theme';
import SendEmail from './SendEmail';

export default function Chat() {
  const isFloating = false;

  const getChatBotProps = () => {
    const props = {};

    if (isFloating) {
      props.floating = true;
      props.floatingIcon = BotAvatarFloatingIcon;
      return props;
    }

    props.height = 'calc(100vh - 80px)';
    props.width = '100%';

    return props;
  }

  const getFlowHelpToOpenCompany = () => {
    return [
      {
        id: 'question_business_interest',
        placeholder: ' ',
        avatar: BotAvatarStar,
        asMessage: true,
        component: (<div>Legal! &#128512; Quais a área de atividade você tem interesse?</div>),
        trigger: 'question_business_interest_2'
      },
      {
        id: 'question_business_interest_2',
        placeholder: ' ',
        asMessage: true,
        component: (<div>Escolha até 3 opções &#128522;. Quando terminar é só clicar em OK</div>),
        trigger: 'business_areas_multi'
      },
      {
        id: 'business_areas_multi',
        placeholder: ' ',
        waitAction: true,
        trigger: 'question_themes',
        component: <BusinessArea />,
      },
      {
        id: 'question_themes',
        placeholder: ' ',
        message: ({ steps }) => `Legal! ${steps.name.message}, quais são os temas de conhecimento que você se interessa mais?`,
        trigger: 'question_themes_options'
      },
      {
        id: 'question_themes_options',
        placeholder: ' ',
        asMessage: true,
        component: (<div>Escolha até 3 opções &#128522;. Quando terminar é só clicar em OK</div>),
        trigger: 'themes'
      },
    ];
  }

  const getFlowCompanyUnstructured = () => {
    return [
      {
        id: 'question_business_areas',
        placeholder: ' ',
        avatar: BotAvatarStar,
        asMessage: true,
        component: (<div>Legal! &#128512; Qual é a área do seu negócio?</div>),
        trigger: 'business_areas'
      },
      {
        id: 'business_areas',
        placeholder: ' ',
        options: getBusinessAreasOptions({ trigger: 'question_company_problems' })
      },
      {
        id: 'question_company_problems',
        placeholder: ' ',
        message: 'Anotado! Quais são as maiores dificuldades da sua empresa hoje?',
        trigger: 'question_themes_options'
      },
    ]
  }

  return (
    <ThemeProvider theme={StyledChat}>
      <ChatBot
        {...getChatBotProps()}
        botAvatar={BotAvatar}
        hideUserAvatar={true}
        enableMobileAutoFocus={true}
        headerTitle={'Atendimento'}
        placeholder='Digite aqui'
        inputStyle={{ fontFamily: fontFamily, color: primary.main }}
        bubbleOptionStyle={BubbleOptionStyle}
        steps={[
          {
            id: 'start',
            asMessage: true,
            component: (<div>Olá! Eu sou o <b>Ari</b>, o atendente virtual do Sebrae</div>),
            trigger: 'hello_2',
            placeholder: ' '
          },
          {
            id: 'hello_2',
            trigger: 'hello_3',
            message: 'Vou te ajudar a encontrar os melhores cursos, eventos e palestras para alavancar o seu negócio e seus estudos sobre empreendedorismo',
            placeholder: ' '
          },
          {
            id: 'hello_3',
            trigger: 'options_start',
            asMessage: true,

            component: (<div>Vamos começar o <b>atendimento</b>?</div>),
            placeholder: ' '
          },
          {
            id: 'options_start',
            placeholder: ' ',
            options: [
              { value: 'option_start', label: 'Ok! Vamos começar', trigger: 'question_name' },
              { value: 'option_finish', label: 'Não, obrigado', trigger: 'question_finish' },
            ]
          },
          {
            id: 'question_name',
            placeholder: ' ',
            trigger: 'name',
            asMessage: true,
            component: (<div>Beleza! Qual o seu <b>nome</b>?</div>),
          },
          {
            id: 'name',
            user: true,
            placeholder: 'Digite aqui o seu nome',
            trigger: 'get_info'
          },
          {
            id: 'get_info',
            placeholder: ' ',
            message: 'Ok, {previousValue}! Agora preciso saber algumas informações',
            trigger: 'profile_1',
            avatar: BotAvatarHappy
          },
          {
            id: 'profile_1',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Qual o seu tipo de perfil <b>empreendedor</b>?</div>),
            trigger: 'profile_2'
          },
          {
            id: 'profile_2',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Escolha o que mais se aproxima da sua <b>situação atual</b></div>),
            trigger: 'profile'
          },
          {
            id: 'profile',
            placeholder: ' ',
            options: [
              getProfileMeta('1', 'question_cnpj'),
              getProfileMeta('2', 'question_business_areas'),
              getProfileMeta('3', 'question_business_interest'),
              getProfileMeta('4', 'question_themes'),
            ]
          },
          ...getFlowCompanyUnstructured(),
          ...getFlowHelpToOpenCompany(),
          {
            id: 'question_cnpj',
            placeholder: ' ',
            avatar: BotAvatarStar,
            asMessage: true,
            component: (<div>Legal! &#128512; Qual o <b>CNPJ</b> da empresa?</div>),
            trigger: 'cnpj'
          },
          {
            id: 'cnpj',
            user: true,
            placeholder: 'Digite o CNPJ...',
            trigger: 'question_company_problems',
            validator: (cnpj) => {
              if (!isCNPJValid(cnpj)) {
                return 'O CNPJ informado é inválido!';
              }

              return true;
            },
          },
          {
            id: 'question_company_problems',
            placeholder: ' ',
            message: 'Anotado! Quais são as maiores dificuldades da sua empresa hoje?',
            trigger: 'question_themes_options'
          },
          {
            id: 'question_themes_options',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Escolha até 3 opções &#128522;. Quando terminar é só clicar em OK</div>),
            trigger: 'themes'
          },
          {
            id: 'themes',
            placeholder: ' ',
            waitAction: true,
            trigger: 'answer_themes',
            component: <CompanyDifficult />,
          },
          {
            id: 'answer_themes',
            placeholder: ' ',
            avatar: BotAvatarHappy,
            asMessage: true,
            component: (<div><b>Obrigado</b> por responder todas as perguntas! 🎉</div>),
            trigger: 'select_solutions'
          },
          {
            id: 'select_solutions',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Estou selecionando as <b>soluções ideais para você</b></div>),
            trigger: 'question_email'
          },
          {
            id: 'question_email',
            trigger: 'email',
            placeholder: ' ',
            message: ({ steps }) => `Qual é o seu e-mail, ${steps.name.message}? Vou precisar dele para te enviar as melhores soluções do Sebrae para alavancar seu negócio`,
          },
          {
            id: 'email',
            user: true,
            placeholder: 'Digite o seu e-mail...',
            validator: (email) => {
              if (!isEmailValid(email)) {
                return 'O e-mail informado é inválido!';
              }

              return true;
            },
            trigger: 'question_cpf'
          },
          {
            id: 'question_cpf',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Agora preciso saber o seu <b>CPF</b></div>),
            trigger: 'cpf'
          },
          {
            id: 'cpf',
            trigger: 'answer_cpf',
            user: true,
            placeholder: 'Digite o seu CPF...',
            validator: (cpf) => {
              if (!isCPFValid(cpf)) {
                return 'O CPF informado é inválido!';
              }

              return true;
            },
          },
          {
            id: 'answer_cpf',
            placeholder: ' ',
            avatar: BotAvatarHappy,
            asMessage: true,
            component: (<div>Ok! <b>Muito obrigado</b></div>),
            trigger: 'question_use_terms'
          },
          {
            id: 'question_use_terms',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Antes de você receber as melhores soluções para te ajudar na sua jornada empreendedora, preciso que você me diga se concorda com os <b>termos de uso e política de privacidade</b></div>),
            trigger: 'call_modal_agree_use_term'
          },
          {
            id: 'call_modal_agree_use_term',
            trigger: 'is_agree_use_term',
            placeholder: ' ',
            asMessage: true,
            component: <UseTermDialog />
          },
          {
            id: 'is_agree_use_term',
            placeholder: ' ',
            waitAction: true,
            component: <AgreeUseTerm />,
            trigger: 'load_products'
          },
          {
            id: 'load_products',
            trigger: 'show_msg_products',
            placeholder: ' ',
            waitAction: true,
            asMessage: true,
            component: <LoadingProduct triggerFallback='no_products' />
          },
          {
            id: 'show_msg_products',
            placeholder: ' ',
            avatar: BotAvatarStar,
            message: 'Aqui está a lista de cursos e palestras que te ajudarão com as suas maiores dificuldades hoje.',
            trigger: 'show_msg_products_2'
          },
          {
            id: 'show_msg_products_2',
            placeholder: ' ',
            avatar: BotAvatarStar,
            asMessage: true,
            component: (<div>Você pode clicar em cada um deles para se <b>inscrever agora mesmo</b> e não perder tempo:</div>),
            trigger: 'show_products'
          },

          {
            id: 'show_products',
            trigger: 'send_products_email',
            component: <Product />,
          },
          {
            id: 'send_products_email',
            trigger: 'service_rating',
            placeholder: ' ',
            asMessage: true,
            waitAction: true,
            component: <SendEmail />,
          },
          {
            id: 'no_products',
            trigger: 'no_products_call_service_rating',
            placeholder: ' ',
            message: 'Ops! Infelizmente não consegui identificar nenhuma solução baseada no seu perfil ou região.'
          },
          {
            id: 'no_products_call_service_rating',
            trigger: 'service_rating',
            placeholder: ' ',
            asMessage: true,
            component: (<div>Como você <b>avalia</b> o meu atendimento virtual ?</div>)
          },
          {
            id: 'service_rating',
            trigger: 'thank_you_service_rating',
            placeholder: ' ',
            waitAction: true,
            component: <ServiceRatingSlider />,
          },
          {
            id: 'thank_you_service_rating',
            placeholder: ' ',
            message: 'Obrigado pela sua opinião, vai me ajudar a melhorar o meu atendimento',
            trigger: 'question_finish',
          },
          {
            id: 'question_finish',
            placeholder: ' ',
            options: [
              { value: 'option_finish', label: 'Finalizar e voltar para o começo', trigger: 'start' }
            ]
          },
        ]}
      />
    </ThemeProvider>
  );
}